import React, { useRef } from "react";
import { TableRowData } from "./data-table";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import { MovementActivity } from "../lib/api";
import { format } from "date-fns";

const BLANK = "_______________________________";
const BLANK_ITEM_ROWS = 15;

interface LostItemsDetailsDrawerProps {
  movementData: MovementActivity | undefined;
  lostItemsDetails: TableRowData | null;
  blank?: boolean;
  onClose: () => void;
}

const LostItemsDetailsDrawer: React.FC<LostItemsDetailsDrawerProps> = ({
  movementData,
  lostItemsDetails,
  blank = false,
  onClose,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const items = movementData?.items ?? [];

  const siteName = blank
    ? BLANK
    : movementData?.sourceSite
      ? [
          movementData.sourceSite.customer?.fullName,
          movementData.sourceSite.name,
        ]
          .filter(Boolean)
          .join(" - ")
      : "N/A";

  const getLocationLine = (
    entity:
      | { street?: string; ward?: string; district?: string; region?: string }
      | null
      | undefined,
  ) =>
    entity
      ? [entity.street, entity.ward, entity.district, entity.region]
          .filter(Boolean)
          .join(", ") || "N/A"
      : "N/A";

  const siteLocation = blank
    ? BLANK
    : getLocationLine(movementData?.sourceSite ?? undefined);

  const referenceNo = blank
    ? BLANK
    : movementData?.reference
      ? movementData.reference
      : "N/A";
  const displayDate = blank
    ? BLANK
    : movementData?.plannedDate
      ? format(new Date(movementData.plannedDate), "MMM dd, yyyy")
      : movementData?.createdAt
        ? format(new Date(movementData.createdAt), "MMM dd, yyyy")
        : "N/A";

  const preparedBy = blank ? BLANK : movementData?.initiator?.fullName || "N/A";

  const approvedBy = blank
    ? BLANK
    : (movementData?.approvedBy ?? movementData?.approved_by ?? BLANK);
  const authorizedDate = blank
    ? BLANK
    : movementData?.plannedDate
      ? format(new Date(movementData.plannedDate), "MMM dd, yyyy")
      : BLANK;

  const captureDate = blank
    ? BLANK
    : movementData?.createdAt
      ? format(new Date(movementData.createdAt), "MMM dd, yyyy hh:mm a")
      : "N/A";

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title = blank
      ? "Blank Lost Items Note"
      : `Lost Items at ${siteName}`;
    window.print();

    document.title = originalTitle;
    if (parent) parent.insertBefore(el, next);
    else el.remove();
  };

  return (
    <SidePanel
      open={!!lostItemsDetails}
      onClose={onClose}
      width="w-[900px]"
      layer="overlay"
    >
      {lostItemsDetails && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={
              blank ? "Blank Lost Items Note" : `Lost Items at ${siteName}`
            }
            onClose={onClose}
            actions={[
              {
                label: "Print",
                icon: Icons.Print,
                onClick: handlePrint,
                pinned: true,
              },
            ]}
          />

          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={
                  isMobile ? "w-full p-3 bg-gray-50" : "w-full p-6 bg-gray-50"
                }
              >
                <ScaledPreview>
                  <div
                    ref={printRef}
                    className="print-area p-4 bg-white shadow-lg rounded-lg"
                  >
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-950 mb-2 border-b-2 border-gray-950 inline-block mx-auto">
                        LOST ITEMS NOTE
                      </h1>
                    </div>
                    <p className="text-gray-950 text-right">
                      No: {referenceNo}
                    </p>
                    <div className="text-center mb-4 border-b-2 border-gray-950 pb-2">
                      <img
                        src="/images/logo.png"
                        alt="Logo"
                        className="h-12 mx-auto mb-2"
                      />
                      <p className="text-sm text-gray-950">
                        Dealers in scaffolding & formwork (sale, rental and
                        fixing, cradle & scissor lift rental and sale)
                      </p>
                      <p className="text-sm text-gray-950">
                        Office: Plot 161, Block 161, Wazo, Tegeta
                      </p>
                      <p className="text-sm text-gray-950">
                        P.O Box 35854, Dar es Salaam, Tanzania. Sales:
                        +255(0)780 973 724 Warehouse: +255(0)780 973 728
                      </p>
                      <p className="text-sm text-gray-950">
                        Email: info@waposcaffolding.com Website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-4 text-sm">
                      <div className="space-y-2">
                        <p className="text-gray-950">
                          <span className="font-semibold">Date:</span>{" "}
                          <span className="font-medium">{displayDate}</span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">Site:</span>{" "}
                          <span className="font-medium">{siteName}</span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">Site Location:</span>{" "}
                          <span className="font-medium">{siteLocation}</span>
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-gray-950">
                          <span className="font-semibold">TIN NO:</span>{" "}
                          <span className="font-medium">124-154-243</span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">VRN:</span>{" "}
                          <span className="font-medium">40-028-187-U</span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">Prepared by:</span>{" "}
                          <span className="font-medium">{preparedBy}</span>
                        </p>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="text-center text-gray-950 font-semibold mb-2">
                        ITEMS LOST AT SITE
                      </div>
                      <table className="w-full border border-gray-950 text-sm">
                        <thead>
                          <tr className="bg-gray-950">
                            <th className="text-white p-2 w-[70px] text-left">
                              No:
                            </th>
                            <th className="text-white p-2 text-left">Item</th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Qty
                            </th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Unit
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {blank ? (
                            Array.from({ length: BLANK_ITEM_ROWS }).map(
                              (_, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                </tr>
                              ),
                            )
                          ) : items.length > 0 ? (
                            items.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {String(idx + 1).padStart(2, "0")}.
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {[
                                    item.inventory?.description || "N/A",
                                    item.inventory?.length === "undefined" ||
                                    item.inventory?.length == null
                                      ? "---"
                                      : item.inventory.length,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {item.totalQuantity ??
                                    item.missedQuantity ??
                                    item.missed_quantity ??
                                    item.goodQuantity ??
                                    0}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  Pc.
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={4}
                                className="border border-gray-950 px-2 py-6 text-center text-gray-950"
                              >
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {!blank && movementData?.notes?.trim() && (
                      <div className="mt-6 text-sm text-gray-950">
                        <p>
                          <span className="font-bold">Notes: </span>
                          {movementData.notes.trim()}
                        </p>
                      </div>
                    )}

                    <div className="mt-6 text-sm text-gray-950 space-y-2">
                      <p className="font-bold text-lg">Charges:</p>
                      <p>
                        Lost Equipment: Will be charged for at the selling price
                        of the item.
                      </p>
                      <p>
                        All transactions are subject to our standard terms of
                        trade which can be found on our website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>

                    <div className="mt-10 grid grid-cols-2 gap-6 text-xs text-gray-950">
                      <div className="space-y-3">
                        <p className="font-semibold">SITE REPRESENTATIVE:</p>
                        <p>Name: _________________________________</p>
                        <p>Signature: ________________________</p>
                        <p>Date: _______________________________</p>
                      </div>
                      <div className="space-y-3">
                        <p className="font-semibold">AUTHORIZED BY</p>
                        <p>Name: {approvedBy}</p>
                        <p>Signature: ________________________</p>
                        <p>Date: {authorizedDate}</p>
                      </div>
                    </div>

                    <div className="flex justify-between mt-8 text-sm text-gray-950">
                      <p>
                        Capture Date & Time:{" "}
                        <span className="font-semibold">{captureDate}</span>
                      </p>
                      <p>
                        Print Date:{" "}
                        <span className="font-semibold">
                          {format(new Date(), "MMM dd, yyyy")}
                        </span>
                      </p>
                    </div>
                  </div>
                </ScaledPreview>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default LostItemsDetailsDrawer;
